import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Button, Card, TextField } from '../components/ui';
import { ApiClientError } from '../api/client';
import { useForgotPassword } from '../api/auth';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function ForgotPasswordPage() {
  const forgotPassword = useForgotPassword();
  const [email, setEmail] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    if (!EMAIL_RE.test(email.trim())) {
      setFieldError('Enter a valid email address.');
      return;
    }
    setFieldError(null);

    try {
      await forgotPassword.mutateAsync({ email: email.trim() });
      setSent(true);
    } catch (err) {
      setError(
        err instanceof ApiClientError && err.httpStatus < 500
          ? err.message
          : 'Could not send the reset email. Please try again.',
      );
    }
  }

  return (
    <div className="ne-auth">
      <Card className="ne-auth__card">
        <h1 className="ne-auth__title">Forgot password</h1>
        <p className="ne-auth__subtitle">We'll email you a link to reset it.</p>
        {sent ? (
          <p style={{ margin: 0 }}>
            If an account exists for <strong>{email.trim()}</strong>, a reset link has been sent. It
            expires in 1 hour.
          </p>
        ) : (
          <form className="ne-auth__form" onSubmit={onSubmit} noValidate>
            <TextField
              label="Email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setFieldError(null);
              }}
              error={fieldError ?? undefined}
            />
            {error ? (
              <p className="ne-auth__error" role="alert">
                {error}
              </p>
            ) : null}
            <Button type="submit" disabled={forgotPassword.isPending}>
              {forgotPassword.isPending ? 'Sending…' : 'Send reset link'}
            </Button>
          </form>
        )}
        <p className="ne-auth__foot">
          <Link to="/login">Back to sign in</Link>
        </p>
      </Card>
    </div>
  );
}
